import React from "react";
import Headshot from "@components/Player/Headshot";
import Section from "@components/Chatbot/Summary/Section";
import DummyGPSSection from "@components/Chatbot/Summary/GPSSection";
import DummyForceplateSection from "@components/Chatbot/Summary/ForceplateSection";
import DummyTestingSection from "@components/Chatbot/Summary/TestingSection";
import { useHeadshot } from "@hooks/useHeadshot";
import CitationButton from "./CitationButton";
import type { Citation } from "../types";

interface SummaryCardProps {
    payload: any;
    animate?: boolean;
    citations?: Citation[];
}

const ProfileSection: React.FC<{ payload: any }> = ({ payload }) => {
    const profile = payload?.profile;
    const gps = payload?.gps;
    const headshotSrc = useHeadshot(profile);

    const getLatestInjury = () => {
        const injuries = profile?.injuries;
        if (!injuries || injuries.length === 0) {
            return { injury_type: "N/A", injury_start_date: "N/A", injury_end_date: "N/A" };
        }
        return injuries[0];
    };

    return (
        <Section title="Player Profile">
            <div className="chat-summary-player-info-container">
                <div className="chat-summary-player-headshot-container">
                    <div style={{ width: "125px" }}>
                        {(profile?.headshot || profile?.headshot_image_url) && (
                            <Headshot
                                headshot={headshotSrc}
                                team_logo={profile.team_logo}
                                useOverlay={profile.use_overlayed_headshot || false}
                                compact={true}
                            />
                        )}
                    </div>
                </div>
                <div className="chat-summary-player-details">
                    <div className="chat-summary-player-detail-row">
                        <p className="chat-summary-player-detail-text">
                            <span className="chat-summary-player-number">#{profile?.number}</span>
                            <span className="chat-summary-player-name">{profile?.full_name}</span>
                            <span className="chat-summary-player-position">({profile?.position})</span>
                        </p>
                    </div>
                    <div className="chat-summary-player-detail-row">
                        <p className="chat-summary-player-detail-text">
                            Top Speed: {profile?.top_speed ?? "N/A"} mph
                        </p>
                    </div>
                    <div className="chat-summary-player-detail-row">
                        <p className="chat-summary-player-detail-text">
                            Days Trained: {gps?.metadata?.trainings?.count ?? "-"} / {gps?.metadata?.trainings?.total ?? "-"}
                        </p>
                    </div>
                    <div className="chat-summary-player-detail-row">
                        <p className="chat-summary-player-detail-text">
                            Games Played: {gps?.metadata?.games?.count ?? "-"} / {gps?.metadata?.games?.total ?? "-"}
                        </p>
                    </div>
                </div>
                <div className="chat-summary-player-details">
                    <div className="chat-summary-player-detail-row">
                        <p className="chat-summary-player-detail-text">
                            Injuries: {profile?.injuries?.length ?? 0}
                        </p>
                    </div>
                    <div className="chat-summary-player-detail-row">
                        <p className="chat-summary-player-detail-text">
                            Latest Injury: {profile?.injuries?.length ? getLatestInjury().injury_type : "N/A"}
                            {!!profile?.injuries?.length && (
                                <span style={{ fontSize: "12px", marginLeft: "10px", textAlign: "center", marginTop: "1.5px" }}>
                                    ({getLatestInjury().injury_start_date} - {getLatestInjury().injury_end_date || "Present"})
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </div>
            <div className="chat-summary-bullets">
                <p style={{ fontSize: "16px", fontWeight: 500, textAlign: "left", marginBottom: 4 }}>Overall Insights:</p>
                {payload?.overview?.bullets?.map((bullet: any, index: number) => (
                    // --bullet-i (own stagger) is separate from the inherited section --i so the bullet
                    // delay can be offset by the profile section's own arrival — see styles.css.
                    <p key={index} className="chat-summary-bullet" style={{ fontSize: "14px", "--bullet-i": index } as React.CSSProperties}>
                        • {bullet?.insight ?? "N/A"}
                    </p>
                ))}
            </div>
        </Section>
    );
};

const renderSection = (sectionKey: string, payload: any, index: number): React.ReactNode => {
    // --i is the section's position in section_order, driving the section-by-section "assembling" cascade.
    const style = { "--i": index } as React.CSSProperties;
    switch (sectionKey) {
        case "profile":
            return (
                <div key={sectionKey} className="chat-summary-section chat-summary-profile-section" style={style}>
                    <ProfileSection payload={payload} />
                </div>
            );
        case "gps":
            return (
                <div key={sectionKey} className="chat-summary-section chat-summary-tech-stream-section" style={style}>
                    <DummyGPSSection summaryContent={payload} />
                </div>
            );
        case "forceplate":
            return (
                <div key={sectionKey} className="chat-summary-section chat-summary-tech-stream-section" style={style}>
                    <DummyForceplateSection summaryContent={payload} />
                </div>
            );
        case "testing":
            return (
                <div key={sectionKey} className="chat-summary-section chat-summary-tech-stream-section" style={style}>
                    <DummyTestingSection summaryContent={payload} />
                </div>
            );
        default:
            return null;
    }
};

const SummaryCard: React.FC<SummaryCardProps> = ({ payload, animate, citations }) => {
    const sectionOrder: string[] = payload?.section_order ?? ["profile", "gps", "testing", "forceplate"];

    return (
        <div className={`chat-summary-card-container${animate ? " chat-v2-artifact-animate" : ""}`}>
            <div className="chat-summary-header-section">
                <p className="chat-summary-header-title">
                    {payload?.overview?.title ?? "Summary"}
                </p>
                <p className="chat-summary-header-subtitle">
                    {payload?.overview?.subtitle ?? ""}
                </p>
            </div>
            {sectionOrder.map((sectionKey, index) => renderSection(sectionKey, payload, index))}
            {citations && citations.length > 0 && (
                <div className="chat-summary-citation-footer">
                    <CitationButton citations={citations} />
                </div>
            )}
        </div>
    );
};

export default SummaryCard;
